import { useDrag, useDrop } from 'react-dnd';
import { WidgetId } from 'widgets/WidgetId';
import { RefObject, useState } from 'react';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useTilesConfigSlice } from 'widgets/tiles/store/config/configSlice';
import { useLogger } from 'logging/writers/hooks';

export const TILE_DRAG_SOURCE = 'TILE';

export interface TileDragItem {
    tilesWidgetKey: string;
    tileWidgetId: WidgetId;
}

export function useTilesWidgetTiles() {
    const { widgetKey } = useWidgetContext();
    const logger = useLogger('useTilesWidgetTiles');

    const {
        config: { tiles },
        dispatchConfigChanged,
    } = useTilesConfigSlice();

    const [lastDropped, setLastDropped] = useState<WidgetId | undefined>(
        undefined
    );

    const updateTiles = (nextTiles: WidgetId[]) => {
        dispatchConfigChanged({ tiles: nextTiles });
    };

    const moveTile = (dropped: TileDragItem, target: TileDragItem) => {
        logger.debug('Move Tile', { widgetKey, dragged: dropped, target });

        const draggedIndex = tiles.indexOf(dropped.tileWidgetId);
        const targetIndex = tiles.indexOf(target.tileWidgetId);

        if (draggedIndex > -1 && targetIndex === draggedIndex + 1) {
            // Dragging to next one one => swap
            const nextPendingValues = [...tiles];
            nextPendingValues[draggedIndex] = target.tileWidgetId;
            nextPendingValues[targetIndex] = dropped.tileWidgetId;
            updateTiles(nextPendingValues);
        } else {
            const nextPendingValues = tiles.filter(
                (p) => p.widgetKey !== dropped.tileWidgetId.widgetKey
            );
            const nextTargetIndex =
                nextPendingValues.indexOf(target.tileWidgetId) +
                (targetIndex > draggedIndex ? 1 : 0);
            nextPendingValues.splice(nextTargetIndex, 0, dropped.tileWidgetId);

            updateTiles(nextPendingValues);
        }

        setLastDropped(dropped.tileWidgetId);
    };

    const removeTile = (tileWidgetKey: string) => {
        logger.debug('Remove Tile', { widgetKey, tileWidgetKey });

        const nextPendingValues = tiles.filter(
            (p) => p.widgetKey !== tileWidgetKey
        );

        updateTiles(nextPendingValues);
    };

    const appendTile = (dropped: TileDragItem) => {
        logger.debug('Append Tile', { widgetKey, dragged: dropped });
        const nextPendingValues = tiles.filter(
            (p) => p.widgetKey !== dropped.tileWidgetId.widgetKey
        );
        nextPendingValues.push(dropped.tileWidgetId);
        updateTiles(nextPendingValues);
        setLastDropped(dropped.tileWidgetId);
    };

    return { tiles: tiles, moveTile, removeTile, appendTile, lastDropped };
}

export function useTileDrop(
    tileDragDropItem: TileDragItem,
    ref: RefObject<HTMLDivElement>,
    moveTile: (
        draggedTileDragItem: TileDragItem,
        targetTileDragItem: TileDragItem
    ) => void
) {
    const [{ canDrop }, drop] = useDrop({
        accept: TILE_DRAG_SOURCE,
        collect(monitor) {
            const target = monitor.getItem<TileDragItem>();
            const canDrop =
                target &&
                target.tileWidgetId &&
                target.tileWidgetId.widgetKey !==
                    tileDragDropItem.tileWidgetId.widgetKey;

            return {
                canDrop: monitor.isOver() && canDrop,
            };
        },

        drop(item: TileDragItem) {
            if (!ref.current) {
                return;
            }

            if (item.tileWidgetId === tileDragDropItem.tileWidgetId) {
                return;
            }

            moveTile(item, tileDragDropItem);

            return tileDragDropItem;
        },
    });
    return { canDrop, drop };
}

export function useTileBackgroundDrop(
    ref: RefObject<HTMLDivElement>,
    appendTile: (draggedTileDragItem: TileDragItem) => void
) {
    const { widgetKey: tilesWidgetKey } = useWidgetContext();
    const [{ canDrop, isOver }, drop] = useDrop({
        accept: TILE_DRAG_SOURCE,
        collect(monitor) {
            return {
                canDrop: monitor.canDrop(),
                isOver: monitor.isOver({ shallow: true }),
            };
        },

        drop(item: TileDragItem, monitor) {
            if (!ref.current) {
                return;
            }

            if (!monitor.isOver({ shallow: true })) {
                return;
            }

            appendTile(item);

            return { tilesWidgetKey };
        },
    });
    return { canDrop, isOver, drop };
}

export function useTileDrag(
    tileDragDropItem: TileDragItem,
    removeTile: () => void
) {
    const [{ isDragging }, drag] = useDrag({
        type: TILE_DRAG_SOURCE,
        item: () => tileDragDropItem,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end(item, monitor) {
            const dropResult = monitor.getDropResult<TileDragItem>();

            if (
                dropResult &&
                dropResult.tilesWidgetKey &&
                dropResult.tilesWidgetKey !== tileDragDropItem.tilesWidgetKey
            ) {
                removeTile();
            }
        },
    });

    return { isDragging, drag };
}
